// ── useAssistant hook (5C) ────────────────────────────────────────────────────
// Manages conversation state, SSE streaming, pills, and undo for the
// contextual assistant panel.

import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { defaultTtsProvider, extractSpeakableText } from '../lib/tts.js';
import { api } from '../lib/api.js';

const TTS_MUTED_KEY = 'evo-assistant-muted';
// Circuit breaker: si play() o el fetch de audio fallan una vez, se corta de
// pedir audio hasta reactivación manual — evita seguir pagando por audio que
// nunca suena (incidente real: 9.400 caracteres de ElevenLabs facturados sin
// sonar ni una vez, por un CSP que bloqueaba blob:). Nunca se resetea solo.
const TTS_BROKEN_KEY = 'evo-assistant-tts-broken';
const TTS_BROKEN_MSG_KEY = 'evo-assistant-tts-broken-msg';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AssistantMessage {
  id: number;
  role: string;       // 'user' | 'assistant' | 'tool_call' | 'tool_result' | 'tool_proposal'
  content: string;
  toolName: string | null;
  toolStatus: string | null;
  isUndone: number;
  humanSummary: string | null;
  createdAt: string;
  undoable: boolean;
  // Presente solo cuando role === 'tool_proposal' (Evo Brain propose-then-approve)
  proposal?: {
    taskId: string;
    turnGroupId: string;
    verb: ProposalVerb;
    summary: ProposalSummary;
    status: 'pending' | 'approved' | 'rejected';
  } | null;
}

export type ProposalVerb = 'create' | 'edit' | 'send' | 'money' | 'delete' | 'publish';
export interface ProposalChange { field: string; from: string | null; to: string }
export interface ProposalSummary {
  sentence: string;
  changes?: ProposalChange[];
  irreversible?: boolean;
  warning?: string;
}

export interface AssistantPill {
  toolName: string;
  label: string;
  requiresInput: boolean;
}

export type StreamingPhase = 'idle' | 'thinking' | 'tool_executing' | 'text';

export interface StreamingState {
  phase: StreamingPhase;
  toolLabel?: string;
  textAccum?: string;  // accumulated text delta during streaming
}

// Mirror of backend AssistantEvent union
type SseEvent =
  | { type: 'thinking' }
  | { type: 'tool_executing'; toolName: string; label: string }
  | { type: 'tool_result'; toolName: string; humanSummary: string; messageId: number; undoable: boolean }
  | {
      type: 'proposal';
      messageId: number;
      taskId: string;
      turnGroupId: string;
      toolName: string;
      verb: ProposalVerb;
      label: string;
      summary: ProposalSummary;
    }
  // voice: versión hablada generada por el server en la misma respuesta —
  // nunca derivada de content acá. null/undefined = silencio.
  | { type: 'text'; content: string; voice?: string | null }
  | { type: 'done'; conversationId: number }
  | { type: 'error'; error: string };

// Query keys to invalidate after tool execution, per entity type
const INVALIDATION_KEYS: Record<string, string[][]> = {
  project:      [['projects'], ['project']],
  lead:         [['leads']],
  contact:      [['contacts'], ['contact']],
  report:       [['reports']],
  email_message: [['email-messages'], ['email-thread'], ['email-detail'], ['email-categories'], ['email-drafts']],
  task:         [['tasks'], ['task']],
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAssistant(
  entityType: string,
  entityId: string | null,
  section?: string | null,
) {
  const qc = useQueryClient();
  const { t } = useTranslation();

  const [messages, setMessages]         = useState<AssistantMessage[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [pills, setPills]               = useState<AssistantPill[]>([]);
  const [streaming, setStreaming]       = useState<StreamingState>({ phase: 'idle' });
  const [error, setError]               = useState<string | null>(null);
  const [isUndoing, setIsUndoing]       = useState(false);

  // ── TTS — voz de salida (Fase 4) ────────────────────────────────────────────
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(TTS_MUTED_KEY) === 'true'; } catch { return false; }
  });
  const mutedRef = useRef(muted);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const [ttsBroken, setTtsBroken] = useState(() => {
    try { return localStorage.getItem(TTS_BROKEN_KEY) === 'true'; } catch { return false; }
  });
  const ttsBrokenRef = useRef(ttsBroken);
  useEffect(() => { ttsBrokenRef.current = ttsBroken; }, [ttsBroken]);
  const [ttsErrorMessage, setTtsErrorMessage] = useState<string | null>(() => {
    try { return localStorage.getItem(TTS_BROKEN_MSG_KEY); } catch { return null; }
  });

  const stopSpeaking = useCallback(() => {
    defaultTtsProvider.stop();
    setIsSpeaking(false);
  }, []);

  const toggleMuted = useCallback(() => {
    setMuted(prev => {
      const next = !prev;
      try { localStorage.setItem(TTS_MUTED_KEY, String(next)); } catch { /* storage unavailable */ }
      if (next) { defaultTtsProvider.stop(); setIsSpeaking(false); }
      return next;
    });
  }, []);

  const reactivateTts = useCallback(() => {
    setTtsBroken(false);
    setTtsErrorMessage(null);
    try {
      localStorage.removeItem(TTS_BROKEN_KEY);
      localStorage.removeItem(TTS_BROKEN_MSG_KEY);
    } catch { /* storage unavailable */ }
  }, []);

  const tripTtsBreaker = useCallback((err: Error) => {
    setIsSpeaking(false);
    setTtsBroken(true);
    setTtsErrorMessage(err.message);
    try {
      localStorage.setItem(TTS_BROKEN_KEY, 'true');
      localStorage.setItem(TTS_BROKEN_MSG_KEY, err.message);
    } catch { /* storage unavailable */ }
  }, []);

  // Visibilidad cuando NO hay línea hablada — antes quedaba en silencio total,
  // indistinguible de "el sistema anda pero no había nada que decir" vs "algo
  // se rompió". null (voice ausente/inválido, Claude no usó deliver_response
  // como se le pidió) y '' (Claude la usó, decidió no decir nada) son casos
  // reales distintos — ver service.ts.
  const [voiceNote, setVoiceNote] = useState<string | null>(null);

  const speakResponse = useCallback((text: string) => {
    if (mutedRef.current) return;
    // Cortado tras una falla real — no se vuelve a pedir audio (ni se gasta)
    // hasta reactivación manual. Ver reactivateTts.
    if (ttsBrokenRef.current) return;
    const spoken = extractSpeakableText(text, {
      linkNote: t('assistant.tts.linkNote'),
      tableNote: t('assistant.tts.tableNote'),
    });
    if (!spoken.trim()) return;
    defaultTtsProvider.speak(spoken, {
      onStart: () => setIsSpeaking(true),
      onEnd: () => setIsSpeaking(false),
      onError: tripTtsBreaker,
    });
  }, [t, tripTtsBreaker]);

  // Tracks which entity we've loaded to detect navigation changes
  const entityKey     = `${entityType}::${entityId ?? ''}`;
  const loadedKeyRef  = useRef('');
  // Prevents concurrent sends without depending on `streaming` state in deps
  const isStreamingRef = useRef(false);
  const abortRef      = useRef<AbortController | null>(null);

  // ── Loaders ───────────────────────────────────────────────────────────────

  const loadConversation = useCallback(async () => {
    const params = new URLSearchParams({ entityType });
    if (entityId) params.set('entityId', entityId);
    try {
      const r = await fetch(`/api/assistant/conversation?${params}`);
      if (!r.ok) return;
      type RawMessage = Omit<AssistantMessage, 'proposal'> & {
        proposal?: { taskId: string; turnGroupId: string; verb: ProposalVerb; summary: ProposalSummary } | null;
      };
      const data = await r.json() as {
        conversationId: number | null;
        messages: RawMessage[];
      };
      // El backend no persiste 'status' dentro de `proposal` — vive en toolStatus
      // (pending|approved|rejected) de la fila. Se reconstruye acá.
      const mapped: AssistantMessage[] = (data.messages ?? []).map(m => ({
        ...m,
        proposal: m.proposal
          ? { ...m.proposal, status: (m.toolStatus as 'pending' | 'approved' | 'rejected' | null) ?? 'pending' }
          : null,
      }));
      setMessages(mapped);
      setConversationId(data.conversationId);
    } catch {
      // non-fatal — conversation may not exist yet
    }
  }, [entityType, entityId]);

  const loadPills = useCallback(async () => {
    const params = new URLSearchParams({
      module:    entityType,
      hasEntity: entityId != null ? '1' : '0',
    });
    try {
      const r = await fetch(`/api/assistant/pills?${params}`);
      if (!r.ok) return;
      const data = await r.json() as { pills: AssistantPill[] };
      setPills(data.pills ?? []);
    } catch {
      // non-fatal
    }
  }, [entityType, entityId]);

  // Reset + reload whenever the entity changes (navigating between pages)
  useEffect(() => {
    if (loadedKeyRef.current === entityKey) return;
    loadedKeyRef.current = entityKey;

    // Cancel any in-flight stream
    abortRef.current?.abort();
    isStreamingRef.current = false;
    defaultTtsProvider.stop();
    setIsSpeaking(false);

    setMessages([]);
    setConversationId(null);
    setError(null);
    setStreaming({ phase: 'idle' });

    void loadConversation();
    void loadPills();
  }, [entityKey, loadConversation, loadPills]);

  // Corta la voz si el panel se desmonta del todo (no solo cambio de entidad)
  useEffect(() => () => defaultTtsProvider.stop(), []);

  // ── Query invalidation ────────────────────────────────────────────────────

  const invalidateRelated = useCallback(() => {
    const keys = INVALIDATION_KEYS[entityType];
    if (!keys) return;
    for (const key of keys) {
      void qc.invalidateQueries({ queryKey: key });
    }
  }, [entityType, qc]);

  // ── Send message (SSE) ────────────────────────────────────────────────────

  const sendMessage = useCallback(async (userMessage: string) => {
    const trimmed = userMessage.trim();
    if (!trimmed || isStreamingRef.current) return;

    // Abort any previous in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    isStreamingRef.current = true;

    // Optimistic user message (negative id = temp, replaced on reload)
    setMessages(prev => [...prev, {
      id:           -Date.now(),
      role:         'user',
      content:      trimmed,
      toolName:     null,
      toolStatus:   null,
      isUndone:     0,
      humanSummary: null,
      createdAt:    new Date().toISOString(),
      undoable:     false,
    }]);
    setError(null);
    setVoiceNote(null);
    setStreaming({ phase: 'thinking' });

    // Versión hablada de esta respuesta, generada por el server en la MISMA
    // llamada, en un campo separado (deliver_response) — nunca derivada de
    // finalAssistantText acá. undefined = el evento 'text' no llegó a fijarlo
    // (no debería pasar); '' = Claude usó deliver_response y decidió no decir
    // nada (silencio válido); string = para hablar.
    let finalVoiceText: string | null | undefined;

    // ── SSE event handler (inline — accesses stable setters + loadConversation) ──
    function handleEvent(event: SseEvent) {
      switch (event.type) {
        case 'thinking':
          setStreaming({ phase: 'thinking' });
          break;

        case 'tool_executing':
          setStreaming({ phase: 'tool_executing', toolLabel: event.label });
          break;

        case 'proposal':
          // Evo Brain: propuesta gated — se agrega a la lista (rol tool_proposal);
          // el panel la agrupa por turnGroupId y renderiza la ApprovalCard.
          setMessages(prev => [...prev, {
            id:           event.messageId,
            role:         'tool_proposal',
            content:      event.summary.sentence,
            toolName:     event.toolName,
            toolStatus:   'pending',
            isUndone:     0,
            humanSummary: event.summary.sentence,
            createdAt:    new Date().toISOString(),
            undoable:     false,
            proposal: {
              taskId:      event.taskId,
              turnGroupId: event.turnGroupId,
              verb:        event.verb,
              summary:     event.summary,
              status:      'pending',
            },
          }]);
          setStreaming({ phase: 'thinking' });
          break;

        case 'tool_result':
          // Append tool result to message list immediately (before done)
          setMessages(prev => [...prev, {
            id:           event.messageId,
            role:         'tool_result',
            content:      event.humanSummary,
            toolName:     event.toolName,
            toolStatus:   'done',
            isUndone:     0,
            humanSummary: event.humanSummary,
            createdAt:    new Date().toISOString(),
            undoable:     event.undoable,
          }]);
          // Claude may call another tool — back to thinking
          setStreaming({ phase: 'thinking' });
          break;

        case 'text':
          finalVoiceText = event.voice;
          setStreaming(prev => ({
            phase:     'text',
            textAccum: (prev.textAccum ?? '') + event.content,
          }));
          break;

        case 'done':
          setConversationId(event.conversationId);
          isStreamingRef.current = false;
          // Reload canonical state from DB (replaces optimistic messages)
          void loadConversation();
          setStreaming({ phase: 'idle' });
          invalidateRelated();
          if (finalVoiceText) {
            setVoiceNote(null);
            speakResponse(finalVoiceText);
          } else if (finalVoiceText === '') {
            setVoiceNote(t('assistant.tts.silentNote'));
          } else {
            // undefined/null — Claude no entregó una línea hablada válida vía
            // deliver_response (no la llamó, o el campo vino mal formado).
            setVoiceNote(t('assistant.tts.missingNote'));
          }
          break;

        case 'error':
          setError(event.error);
          isStreamingRef.current = false;
          setStreaming({ phase: 'idle' });
          break;
      }
    }

    try {
      const response = await fetch('/api/assistant/chat', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ entityType, entityId, message: trimmed, ...(section ? { section } : {}) }),
        signal:  controller.signal,
      });

      if (!response.ok) {
        const body = await response.json() as { error?: string };
        throw new Error(body.error ?? `HTTP ${response.status}`);
      }
      if (!response.body) throw new Error('No response body');

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer    = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by \n\n
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';

        for (const frame of frames) {
          // Strip "data: " prefix
          const raw = frame.startsWith('data: ') ? frame.slice(6) : frame;
          if (!raw.trim()) continue;
          try {
            handleEvent(JSON.parse(raw) as SseEvent);
          } catch {
            // Skip malformed frame
          }
        }
      }
    } catch (err: unknown) {
      if ((err as Error).name === 'AbortError') return;
      setError((err as Error).message ?? 'Error desconocido');
      isStreamingRef.current = false;
      setStreaming({ phase: 'idle' });
    }
  }, [entityType, entityId, section, loadConversation, invalidateRelated, speakResponse]);

  // ── Undo ──────────────────────────────────────────────────────────────────

  const undoMessage = useCallback(async (messageId: number) => {
    if (isUndoing || isStreamingRef.current) return;
    setIsUndoing(true);
    setError(null);
    try {
      const r = await fetch('/api/assistant/undo', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ messageId }),
      });
      if (!r.ok) {
        const body = await r.json() as { error?: string };
        throw new Error(body.error ?? `HTTP ${r.status}`);
      }
      // Optimistic: mark undone locally
      setMessages(prev =>
        prev.map(m => m.id === messageId ? { ...m, isUndone: 1 } : m),
      );
      invalidateRelated();
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setIsUndoing(false);
    }
  }, [isUndoing, invalidateRelated]);

  // ── Evo Brain: approve / reject de propuestas gated ────────────────────────
  // No pasan por SSE/Claude — approve ejecuta in-server (POST /api/tasks/:id/approve
  // bifurca por executor='tool') y reject descarta (POST /:id/reject). Ambos
  // caminos recargan la conversación canónica (trae tool_result + confirmación
  // en el caso de approve) e invalidan Task Manager además de la entidad.

  const [approvingTaskId, setApprovingTaskId] = useState<string | null>(null);
  const [rejectingTaskId, setRejectingTaskId] = useState<string | null>(null);
  const [bulkApproving, setBulkApproving]     = useState(false);
  const [bulkRejecting, setBulkRejecting]     = useState(false);

  const invalidateTasks = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['tasks'] });
  }, [qc]);

  const approveProposal = useCallback(async (taskId: string, remember?: boolean) => {
    if (approvingTaskId || rejectingTaskId) return;
    setApprovingTaskId(taskId);
    setError(null);
    try {
      const r = await fetch(`/api/tasks/${taskId}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ rememberApproval: Boolean(remember) }),
      });
      if (!r.ok) {
        const body = await r.json() as { error?: string };
        throw new Error(body.error ?? `HTTP ${r.status}`);
      }
      await loadConversation();
      invalidateRelated();
      invalidateTasks();
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setApprovingTaskId(null);
    }
  }, [approvingTaskId, rejectingTaskId, loadConversation, invalidateRelated, invalidateTasks]);

  const rejectProposal = useCallback(async (taskId: string) => {
    if (approvingTaskId || rejectingTaskId) return;
    setRejectingTaskId(taskId);
    setError(null);
    try {
      const r = await fetch(`/api/tasks/${taskId}/reject`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!r.ok) {
        const body = await r.json() as { error?: string };
        throw new Error(body.error ?? `HTTP ${r.status}`);
      }
      await loadConversation();
      invalidateTasks();
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setRejectingTaskId(null);
    }
  }, [approvingTaskId, rejectingTaskId, loadConversation, invalidateTasks]);

  // "Aprobar/Rechazar todas" — secuencial (no Promise.all): cada approve muta
  // datos reales; una falla a mitad de grupo no debe dejar el resto a medias
  // sin feedback. El caller (panel) ya filtra a las pendientes reversibles.
  const approveAllProposals = useCallback(async (taskIds: string[]) => {
    if (bulkApproving || bulkRejecting) return;
    setBulkApproving(true);
    setError(null);
    try {
      for (const id of taskIds) {
        const r = await fetch(`/api/tasks/${id}/approve`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!r.ok) {
          const body = await r.json() as { error?: string };
          throw new Error(body.error ?? `HTTP ${r.status}`);
        }
      }
      await loadConversation();
      invalidateRelated();
      invalidateTasks();
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setBulkApproving(false);
    }
  }, [bulkApproving, bulkRejecting, loadConversation, invalidateRelated, invalidateTasks]);

  const rejectAllProposals = useCallback(async (taskIds: string[]) => {
    if (bulkApproving || bulkRejecting) return;
    setBulkRejecting(true);
    setError(null);
    try {
      for (const id of taskIds) {
        const r = await fetch(`/api/tasks/${id}/reject`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!r.ok) {
          const body = await r.json() as { error?: string };
          throw new Error(body.error ?? `HTTP ${r.status}`);
        }
      }
      await loadConversation();
      invalidateTasks();
    } catch (err: unknown) {
      setError((err as Error).message);
    } finally {
      setBulkRejecting(false);
    }
  }, [bulkApproving, bulkRejecting, loadConversation, invalidateTasks]);

  return {
    messages,
    conversationId,
    pills,
    streaming,
    error,
    isUndoing,
    sendMessage,
    undoMessage,
    approveProposal,
    rejectProposal,
    approveAllProposals,
    rejectAllProposals,
    approvingTaskId,
    rejectingTaskId,
    bulkApproving,
    bulkRejecting,
    muted,
    toggleMuted,
    isSpeaking,
    stopSpeaking,
    ttsBroken,
    ttsErrorMessage,
    reactivateTts,
    voiceNote,
  };
}

// ── Aprobaciones permanentes (Settings → Evo Brain) ──────────────────────────

export interface ToolApproval {
  id: number;
  toolName: string;
  label: string;
  grantedAt: string;
}

export function useToolApprovals() {
  return useQuery<{ approvals: ToolApproval[] }>({
    queryKey: ['assistant-tool-approvals'],
    queryFn: () => api.get('/api/assistant/tool-approvals'),
    staleTime: 10_000,
  });
}

export function useRevokeToolApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (toolName: string) => api.delete(`/api/assistant/tool-approvals/${toolName}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['assistant-tool-approvals'] }); },
  });
}
